import { useState } from "react";
import React, { useEffect } from "react";
import Graph from "graphology";
import { SigmaContainer, useLoadGraph } from "@react-sigma/core";
import circular from "graphology-layout/circular";
import forceAtlas2 from "graphology-layout-forceatlas2";
import { cropToLargestConnectedComponent } from "graphology-components";
import { useAtomValue, useSetAtom } from "jotai";
import { useHttpPost } from "./hooks";
import { reloadState, searchState, selectedCategory } from "./JotaiAtoms";
import { NodeImageProgram } from "@sigma/node-image";
import "@react-sigma/core/lib/style.css";
import { GraphEventsController, GraphSettingsController } from "./StoryGraph";
import { AccountInfo } from "./JotaiAtoms";
import * as lodash from "lodash";
import { connectedComponents } from "graphology-components";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import PeopleIcon from "@mui/icons-material/People";

type Node = AccountInfo;
type Edge = [number | number[], number | number[], number];

type GraphData = {
  nodes: Node[];
  edges: Edge[];
};

type UserGraphProps = {
  data: GraphData;
};

function LoadGraph({ data }: { data: GraphData }) {
  const loadGraph = useLoadGraph();
  const graph = new Graph();

  useEffect(() => {
    if (!data) {
      return;
    }

    graph.clear();

    const singleUsers = data.nodes.filter((u) => !u.group);
    const groupedUsers = lodash.groupBy(
      data.nodes.filter((u) => u.group),
      (u) => u.group
    );

    const addNode = (
      accounts: AccountInfo[],
      key: string,
      label: string,
      avatar?: string
    ) => {
      const attrs = {
        size: 25,
        label: label,
        accounts: accounts,
      };
      if (avatar) {
        graph.addNode(key, {
          ...attrs,
          image: avatar,
          type: "image",
        });
      } else {
        graph.addNode(key, {
          ...attrs,
        });
      }
    };

    singleUsers.forEach((node) => {
      const key = node.app + ":" + node.id;
      const label =
        node.name || node.username || node.email || node.id || "<unknown>";
      const avatar = node.avatar;
      addNode([node], key, label, avatar);
    });

    lodash.forEach(groupedUsers, (group, groupName) => {
      const key = groupName;
      let repr =
        group.find((x) => x.name) ||
        group.find((x) => x.username) ||
        group.find((x) => x.email) ||
        group.find((x) => x.id);
      let label = repr
        ? repr.name || repr.username || repr.email || repr.id || "<unknown>"
        : "<unknown>";
      let avatar = group.find((x) => x.avatar)?.avatar;
      addNode(group, key, label, avatar);
    });

    const heaviestEdges = data.edges.sort((a, b) => b[2] - a[2]).slice(0, 300);

    heaviestEdges.forEach(([sourceIdx, targetIdx, weight]) => {
      const sourceGroup = Array.isArray(sourceIdx) ? sourceIdx : [sourceIdx];
      const targetGroup = Array.isArray(targetIdx) ? targetIdx : [targetIdx];

      const source = data.nodes[sourceGroup[0]];
      const target = data.nodes[targetGroup[0]];

      const sourceId = source.group
        ? source.group
        : source.app + ":" + source.id;
      const targetId = target.group
        ? target.group
        : target.app + ":" + target.id;

      const size = Math.min(5, Math.max(2, weight));
      graph.addEdge(sourceId, targetId, { weight: 100, size: size });
    });

    const components = connectedComponents(graph);
    components.forEach((component) => {
      if (component.length < 4) {
        component.forEach((node) => graph.dropNode(node));
      }
    });

    if (graph.nodes().length > 0) {
      const maxDegree = Math.max(
        ...graph.nodes().map((node) => graph.degree(node))
      );
      const p95Array = graph
        .nodes()
        .map((node) => graph.degree(node))
        .sort((a, b) => a - b)
        .slice(Math.floor(graph.nodes().length * 0.95));

      const p95 = p95Array.length > 0 ? p95Array[0] : maxDegree;

      graph.forEachNode((node) => {
        const degree = graph.degree(node);
        const maxSize = 35;
        const size = (degree / p95) * maxSize;
        graph.setNodeAttribute(
          node,
          "size",
          Math.min(maxSize, Math.max(13, size))
        );
      });

      // cropToLargestConnectedComponent(graph);
      circular.assign(graph);
      const settings = forceAtlas2.inferSettings(graph);
      // forceAtlas2.assign(graph, { settings, iterations: 600 });

      forceAtlas2.assign(graph, {
        iterations: 5000,
        settings: {
          gravity: 40, // ⬆️ Pulls nodes toward center
          // scalingRatio: 0.5,        // ⬇️ Reduces repulsion distance
          // strongGravityMode: true,  // ⬆️ Makes gravity more dominant
          // linLogMode: true,         // ⬇️ Tightens clusters
          edgeWeightInfluence: 1, // ⬆️ Keeps edge structure intact
          // barnesHutOptimize: true   // ⚡️ Faster for large graphs
        },
      });
    }

    loadGraph(graph);
  }, [data, loadGraph]);

  return null;
}

function UserGraph() {
  const reload = useAtomValue(reloadState);
  const [data, setData] = React.useState<GraphData>({ nodes: [], edges: [] });
  const [isLoading, setIsLoading] = useState(true);
  const httpPost = useHttpPost();
  const [hoveredNode, setHoveredNode] = useState("");
  const setSearch = useSetAtom(searchState);
  const setSelectedCategory = useSetAtom(selectedCategory);
  const theme = useTheme();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await httpPost("stories", { interactions: true });
        const result = await response.data.interactions;
        setData(result);
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [reload]);

  const handleNodeClick = (node: any) => {
    if (node && node.accounts && node.accounts.length > 0) {
      setTimeout(() => {
        setSelectedCategory("Stories");
        setTimeout(() => {
          setSearch([node.accounts]);
        }, 300);
      }, 0);
    }
  };

  const isEmpty = !data || data.nodes.length === 0;

  if (!isLoading && isEmpty) {
    return (
      <Box
        sx={{
          height: "90vh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <PeopleIcon
          sx={{
            fontSize: "64px",
            color: "var(--text-secondary)",
            opacity: 0.3,
            mb: 1,
          }}
        />
        <Box
          sx={{
            color: "var(--text-primary)",
            fontSize: "16px",
            fontWeight: 500,
          }}
        >
          No collaboration data available
        </Box>
        <Box
          sx={{
            color: "var(--text-secondary)",
            fontSize: "13px",
            maxWidth: "300px",
            textAlign: "center",
          }}
        >
          Collaboration patterns will appear here as people interact on stories
        </Box>
      </Box>
    );
  }

  return (
    <SigmaContainer
      style={{
        height: "90vh",
        width: "100%",
        backgroundColor: theme.palette.mode === 'dark' ? '#121212' : '#ffffff',
      }}
      settings={{
        allowInvalidContainer: true,
        nodeProgramClasses: {
          image: NodeImageProgram,
        },
        renderEdgeLabels: false,
        defaultNodeColor: theme.palette.mode === 'dark' ? '#9aa0a6' : 'grey',
        defaultEdgeColor: theme.palette.mode === 'dark' ? '#5f6368' : '#ccc',
        labelColor: { color: theme.palette.mode === 'dark' ? '#e8eaed' : '#202124' },
      }}
    >
      <GraphSettingsController hoveredNode={hoveredNode} />
      <GraphEventsController
        setHoveredNode={setHoveredNode}
        onNodeClick={handleNodeClick}
      />
      <LoadGraph data={data} />
    </SigmaContainer>
  );
}

export default UserGraph;
